import { Users } from "lucide-react";
import { PageHeading, Section, Field, Steps, Callout, RoleBadge, Diagram, MockRow, MockField, MockSelect, MockButton } from "../shared";

export default function AdminUserAccountsSection() {
  return (
    <div data-testid="guide-admin-user-accounts">
      <PageHeading
        icon={Users}
        title="User Accounts"
        description="The actual logins. Create and edit users, give them a role, link them to a team-member name, reset passwords, and decide whether they can register new tasks."
      />

      <Callout variant="info" title="In plain words">
        A user account is a way to sign in. This is different from a Team Member (which is just a name). Here you create someone's login, choose what they're allowed to do (their role), and connect them to their name so emails reach the right inbox.
      </Callout>

      <Section title="Who can manage it">
        <div className="flex items-center gap-2"><RoleBadge role="admin" /><span className="text-xs text-white/55"><span className="text-white">manage_users</span> for create/edit; <span className="text-white">delete_users</span> for permanent deletion. Both actions also enforce the target-role guard described below.</span></div>
      </Section>

      <Section title="When do I use this?">
        <p className="text-sm text-white/65 leading-relaxed">
          Use it when a new person needs to log in, when someone's job (role) changes, or when somebody forgets their password and needs a reset.
        </p>
      </Section>

      <Section title="Creating a user">
        <Diagram
          frameLabel="Admin → User Accounts → Add User"
          legend={[
            <>Click <span className="text-white">Add User</span> to open the form.</>,
            <>Type a <span className="text-white">username</span> and <span className="text-white">email</span> (both required) and an initial password.</>,
            <>Pick the <span className="text-white">role</span> — the list hides any role at or above your own level.</>,
            <>Optionally link them to a <span className="text-white">team-member name</span> so notifications reach their inbox.</>,
            <>Click <span className="text-white">Save</span>. They can log in straight away.</>,
          ]}
        >
          <MockButton label="Add User" marker={1} />
          <MockRow>
            <MockField label="Username" value="s.khan" marker={2} />
            <MockField label="Email" value="s.khan@org.com" />
          </MockRow>
          <MockRow>
            <MockSelect label="Role" value="General Admin" marker={3} />
            <MockSelect label="Linked team member" value="Sara Khan" marker={4} />
          </MockRow>
          <MockButton label="Save" marker={5} />
        </Diagram>
        <Steps steps={[
          <>Click <span className="text-white">Add User</span>. Fill in username, email, an initial password, role, optional team-member link, and the can-register-tasks flag.</>,
          <>Pick a role — the dropdown won't show roles at or above your own level.</>,
          <>Optionally link a team-member name so notifications addressed to that name reach this login's email.</>,
          <>Save. The new user can log in immediately. Tell them to change their password on first login (Account → Change Password).</>,
        ]} />
      </Section>

      <Section title="Fields on a user">
        <div className="space-y-2">
          <Field name="Username" required="required"><p>Unique login identifier. Case-sensitive on storage but the login form is case-insensitive.</p></Field>
          <Field name="Email" required="required"><p>Used for password reset and (when no team-member binding exists) for notifications.</p></Field>
          <Field name="First name / Last name" required="optional"><p>Displayed in the header and audit trail.</p></Field>
          <Field name="Role" required="required"><p>Pick from the active role list — includes the eight preset roles and any custom roles you have created. The dropdown hides any role at or above your own level (target-role guard).</p></Field>
          <Field name="Linked team member" required="optional"><p>Binds this login to a Team Member name so notifications addressed to that name reach this user's email.</p></Field>
          <Field name="Can register tasks" required="optional"><p>Independent flag. When OFF, the user cannot use the Register form even if their role would otherwise allow it. Useful for read-only senior reviewers.</p></Field>
        </div>
      </Section>

      <Section title="Resetting a user's password">
        <Diagram
          frameLabel="Admin → User Accounts → Reset Password"
          legend={[
            <>Open the user's row and click <span className="text-white">Reset Password</span>.</>,
            <>Type the <span className="text-white">new password twice</span>.</>,
            <>Confirm with <span className="text-white">your own password</span> (this extra check is called step-up).</>,
          ]}
        >
          <MockButton label="Reset Password" marker={1} />
          <MockRow>
            <MockField label="New password" placeholder="••••••••" marker={2} />
            <MockField label="Confirm new password" placeholder="••••••••" />
          </MockRow>
          <MockField label="Your password (step-up)" placeholder="••••••••" marker={3} />
        </Diagram>
        <Steps steps={[
          <>Open the user row → <span className="text-white">Reset Password</span>.</>,
          <>Enter the new password twice.</>,
          <>Confirm with your own password (step-up re-authentication). The new password is bcrypt-hashed and any existing sessions for that user remain logged in until expiry.</>,
        ]} />
      </Section>

      <Callout variant="warning" title="Target-role guard">
        You cannot edit, change the role of, or delete a user whose role level is equal to or higher than yours. This is enforced server-side. A super-admin demoting another super-admin must first arrange for someone else to demote them, or an even-higher actor (dev_admin) must do it.
      </Callout>
      <Callout variant="warning" title="Last super-admin protection">
        The system refuses to remove or demote the final remaining super_admin. There must always be at least one super-admin who can edit the authorization matrix.
      </Callout>
      <Callout variant="info" title="dev_admin is hidden">
        The platform keyholder role <span className="text-white">dev_admin</span> never appears in the role dropdown and cannot be assigned from the UI.
      </Callout>
    </div>
  );
}
